import { invariantResponse } from '@epic-web/invariant'
import {
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
	redirect,
} from '@remix-run/node'
import { Form, useActionData, json, Outlet } from '@remix-run/react'
import { Button } from '#app/components/ui/button.js'
import { getUserId, getCompanyId } from '#app/utils/auth.server.js'
import { useIsPending } from '#app/utils/misc.js'
import { SALES_IMPORT_STATUS } from '#app/constants/sales-import.ts'
import { importSalesData } from '#app/services/sales-import.server.ts'

export async function action({ request }: ActionFunctionArgs) {
	const companyId = await getCompanyId(request)
	const userId = await getUserId(request)
	invariantResponse(companyId, 'Company not found', { status: 404 })
	invariantResponse(userId, 'User not found', { status: 404 })

	const formData = await request.formData()
	const media = formData.get('media')

	if (!media || typeof media === 'string') {
		return json({ error: 'No file uploaded' }, { status: 400 })
	}

	const fileText = await media.text()

	try {
		const result = await importSalesData({
			companyId,
			userId,
			filename: (media as File).name,
			source: 'MANUAL_UPLOAD',
			fileContents: fileText,
		})

		const reviewUrl = `/upload-sales-data/review/${result.salesImport.id}`
		if (result.duplicate) {
			return redirect(`${reviewUrl}?duplicate=1`)
		}

		if (result.unmatchedLineCount > 0 || result.salesImport.status === SALES_IMPORT_STATUS.NEEDS_REVIEW) {
			return redirect(`${reviewUrl}?status=${result.salesImport.status.toLowerCase()}`)
		}

		return redirect(`${reviewUrl}?status=${result.salesImport.status.toLowerCase()}`)
	} catch (error) {
		console.error('Sales import failed', error)
		return json(
			{ error: error instanceof Error ? error.message : 'Failed to import sales data' },
			{ status: 400 },
		)
	}
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	return {}
}

const UploadInventory = () => {
	const data = useActionData<typeof action>()
	const isPending = useIsPending()
	return (
		<div className=" font-regular font-source-sans-pro">
			<div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
				<div className="mx-auto max-w-2xl text-center">
					<h2 className="font-source-sans-pro text-3xl font-black font-bold tracking-tight text-gray-900 sm:text-4xl ">
						Upload sales data
						<br />
					</h2>
					<div className="mt-10 flex items-center justify-center gap-x-6">
						<Form
							className="w-full max-w-[500px]"
							method="post"
							encType="multipart/form-data"
						>
							<div className="mb-8">
								<input
									type="file"
									name="media"
									className="focus:ring-secondary-orange bg-off-white block w-full rounded-md border-0 py-1.5 text-black shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
									accept=".csv"
								/>
							</div>
							<Button>
								{isPending ? 'Uploading Sales data...' : 'Upload Sales Data'}
							</Button>
						</Form>
					</div>
					<p className="mt-8 text-base leading-7 text-gray-600">
						Currently accepts CSV format.
					</p>
					{data?.error && (
						<div className="mt-4 text-red-600">
							<p>{data.error}</p>
						</div>
					)}{' '}
				</div>
			</div>
			<Outlet />
		</div>
	)
}

export default UploadInventory

export function CatchBoundary() {
	return (
		<div>
			<h1></h1>
			<p>Something went wrong - please reload the page or try again</p>
		</div>
	)
}
